import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Shield, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Calendar,
  Scale
} from 'lucide-react';

const TermsOfServicePage = () => {
  const lastUpdated = "December 1, 2024";

  const sections = [
    {
      title: "Acceptance of Terms",
      icon: <CheckCircle className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Agreement to Terms",
          description: "By accessing and using Cricket Scoring's platform, you accept and agree to be bound by the terms and provision of this agreement."
        },
        {
          subtitle: "Modifications",
          description: "We reserve the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms."
        }
      ]
    },
    {
      title: "Service Description",
      icon: <FileText className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Platform Purpose",
          description: "Cricket Scoring provides cricket tournament management, live match scoring, player statistics tracking, and related services for cricket enthusiasts and organizations."
        },
        {
          subtitle: "Service Availability",
          description: "We strive to maintain high service availability but do not guarantee uninterrupted access. We may perform maintenance that temporarily affects service availability."
        }
      ]
    },
    {
      title: "User Accounts",
      icon: <Users className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Account Creation",
          description: "You must create an account to access certain features. You are responsible for maintaining the confidentiality of your account credentials."
        },
        {
          subtitle: "Account Security",
          description: "You are responsible for all activities that occur under your account. Notify us immediately of any unauthorized use or security breaches."
        },
        {
          subtitle: "Account Termination",
          description: "We may terminate or suspend your account at any time for violations of these terms or for any other reason at our sole discretion."
        }
      ]
    },
    {
      title: "Acceptable Use",
      icon: <Shield className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Permitted Use",
          description: "Use the platform for legitimate cricket scoring and tournament management purposes only. Respect other users and maintain appropriate conduct."
        },
        {
          subtitle: "Prohibited Activities",
          description: "Do not use the service for illegal activities, harassment, spam, or any purpose that could harm the platform or other users."
        },
        {
          subtitle: "Data Accuracy",
          description: "Ensure that all cricket data and statistics you input are accurate and truthful to the best of your knowledge."
        }
      ]
    },
    {
      title: "Intellectual Property",
      icon: <Scale className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Platform Ownership",
          description: "The platform, including its design, functionality, and content, is owned by Cricket Scoring and protected by intellectual property laws."
        },
        {
          subtitle: "User Content",
          description: "You retain ownership of cricket data you input, but grant us a license to use, store, and display this data as part of our services."
        },
        {
          subtitle: "License Grant",
          description: "We grant you a limited, non-exclusive license to use the platform for its intended purpose, subject to these terms."
        }
      ]
    },
    {
      title: "Privacy and Data",
      icon: <Shield className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Data Collection",
          description: "We collect and process data as described in our Privacy Policy. By using our services, you consent to such collection and processing."
        },
        {
          subtitle: "Data Security",
          description: "We implement reasonable security measures to protect your data, but cannot guarantee absolute security against all threats."
        },
        {
          subtitle: "Data Sharing",
          description: "We may share aggregated, anonymized data for analytics and improvement purposes, but will not sell your personal information."
        }
      ]
    }
  ];

  const prohibitedActivities = [
    "Attempting to gain unauthorized access to the platform or other users' accounts",
    "Uploading malicious code, viruses, or other harmful content",
    "Interfering with the platform's functionality or performance",
    "Using automated tools to access the service without permission",
    "Harassing, threatening, or intimidating other users",
    "Violating any applicable laws or regulations",
    "Attempting to reverse engineer or copy the platform's code",
    "Using the service for commercial purposes without proper authorization"
  ];

  const limitations = [
    {
      title: "Service Availability",
      description: "We do not guarantee 100% uptime and may perform maintenance that affects service availability."
    },
    {
      title: "Data Accuracy",
      description: "While we strive for accuracy, we cannot guarantee that all cricket statistics and data are completely error-free."
    },
    {
      title: "Third-Party Services",
      description: "We may use third-party services that have their own terms and privacy policies."
    },
    {
      title: "Technical Issues",
      description: "We are not responsible for technical issues beyond our control, such as internet connectivity problems."
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            Please read these terms carefully before using our cricket scoring platform.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Badge variant="outline">
              <Calendar className="h-3 w-3 mr-1" />
              Last Updated: {lastUpdated}
            </Badge>
            <Badge variant="secondary">
              <FileText className="h-3 w-3 mr-1" />
              Legal Document
            </Badge>
          </div>
        </div>

        {/* Introduction */}
        <Card className="mb-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold text-foreground mb-4">Introduction</h2>
            <p className="text-muted-foreground mb-4">
              These Terms of Service ("Terms") govern your use of the Cricket Scoring platform and services. By accessing or using our services, you agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p className="text-muted-foreground">
              If you do not agree to these Terms, please do not use our services. These Terms constitute a legally binding agreement between you and Cricket Scoring.
            </p>
          </CardContent>
        </Card>

        {/* Main Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  {section.icon}
                  <CardTitle className="text-xl">{section.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="space-y-2">
                      <h3 className="font-semibold text-foreground">{item.subtitle}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Prohibited Activities */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <XCircle className="h-5 w-5 text-destructive" />
              <span>Prohibited Activities</span>
            </CardTitle>
            <CardDescription>
              The following activities are strictly prohibited and may result in account termination.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {prohibitedActivities.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 border rounded-lg bg-destructive/5">
                  <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{activity}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Limitations of Liability */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <span>Limitations of Liability</span>
            </CardTitle>
            <CardDescription>
              Important limitations on our liability and your rights.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {limitations.map((limitation, index) => (
                <div key={index} className="space-y-2">
                  <h3 className="font-semibold text-foreground">{limitation.title}</h3>
                  <p className="text-sm text-muted-foreground">{limitation.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Disclaimers */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl">Disclaimers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                <strong>Service "As Is":</strong> The platform is provided "as is" without warranties of any kind, either express or implied.
              </p>
              <p className="text-muted-foreground">
                <strong>No Guarantees:</strong> We do not guarantee that the service will meet your specific requirements or be error-free.
              </p>
              <p className="text-muted-foreground">
                <strong>Third-Party Content:</strong> We are not responsible for content provided by third parties or other users.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Termination */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl">Termination</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                <strong>By You:</strong> You may terminate your account at any time by contacting us or using the account deletion feature.
              </p>
              <p className="text-muted-foreground">
                <strong>By Us:</strong> We may terminate or suspend your account immediately for violations of these Terms or for any other reason.
              </p>
              <p className="text-muted-foreground">
                <strong>Effect of Termination:</strong> Upon termination, your right to use the service ceases immediately, and we may delete your account data.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl">Governing Law</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              These Terms are governed by and construed in accordance with the laws of the jurisdiction where Cricket Scoring is incorporated. Any disputes arising from these Terms or your use of the service shall be resolved in the courts of that jurisdiction.
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mt-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-bold text-foreground mb-4">Questions About These Terms?</h2>
            <p className="text-muted-foreground mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-foreground">
                <strong>Email:</strong> legal@cricketscoring.com
              </p>
              <p className="text-foreground">
                <strong>Address:</strong> 123 Cricket Lane, Sports City, SC 12345
              </p>
              <p className="text-foreground">
                <strong>Phone:</strong> +1 (555) 123-4567
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfServicePage; 